import { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { HookFormContext } from 'components/cef_form/hook-form/providers/HookFormProvider';
import RegisteredAddressSelector from './RegisteredAddressSelector';
import BillingAddressSelector from './BillingAddressSelector';
import HomeAddressSelector from './HomeAddressSelector';
import PreviousAddressSelector from './PreviousAddressSelector';
import {
  addressOneDataModel,
  addressTwoDataModel,
} from '../postcodeSelectors/addressDataModels';

const Step3Routes = ({ dispatch, postcodeSearched, hasPersistedData }) => {
  const {
    watchedFields: {
      companyType,
      companyNumberLookupSuccess,
      addressOne,
      billingAddressMatchesRegistered,
      addressTwo,
      propertyDuration,
    },
  } = useContext(HookFormContext);

  const {
    manualCompanyPostcodeSearched,
    billingPostcodeSearched,
    homePostcodeSearched,
    previousPostcodeSearched,
  } = postcodeSearched;

  const {
    manualCompanyHasPersistedData,
    billingHasPersistedData,
    homeHasPersistedData,
    previousHasPersistedData,
  } = hasPersistedData;

  const setPostcodeSearched = (field, value) =>
    dispatch({ type: 'SET_POSTCODE_SEARCHED', payload: { field, value } });

  const setPostcodeIsReset = (field, value) =>
    dispatch({ type: 'SET_POSTCODE_IS_RESET', payload: { field, value } });

  // Update postcode searched state based on changes in persisted data
  useEffect(() => {
    setPostcodeSearched(
      'manualCompanyPostcodeSearched',
      manualCompanyHasPersistedData,
    );
  }, [manualCompanyHasPersistedData]);

  useEffect(() => {
    setPostcodeSearched('billingPostcodeSearched', billingHasPersistedData);
  }, [billingHasPersistedData]);

  useEffect(() => {
    setPostcodeSearched('homePostcodeSearched', homeHasPersistedData);
  }, [homeHasPersistedData]);

  useEffect(() => {
    setPostcodeSearched('previousPostcodeSearched', previousHasPersistedData);
  }, [previousHasPersistedData]);

  const mainRoute = [
    <RegisteredAddressSelector
      key="registeredAddress"
      companyNumberLookupSuccess={companyNumberLookupSuccess}
      companiesHouseAddress={companyNumberLookupSuccess ? addressOne : null}
      postcodeSearched={manualCompanyPostcodeSearched}
      setPostcodeSearched={value =>
        setPostcodeSearched('manualCompanyPostcodeSearched', value)
      }
      setPostcodeIsReset={value =>
        setPostcodeIsReset('manualCompanyPostcodeIsReset', value)
      }
      manualCompanyAddressDataModel={
        // Don't need to set a manual address if company lookup was successful as address set
        // automatically
        companyNumberLookupSuccess ? null : addressOneDataModel
      }
      persistedData={addressOne}
    />,
    <BillingAddressSelector
      key="billingAddress"
      showPostcodeSelector={!billingAddressMatchesRegistered}
      postcodeSearched={billingPostcodeSearched}
      setPostcodeSearched={value =>
        setPostcodeSearched('billingPostcodeSearched', value)
      }
      setPostcodeIsReset={value =>
        setPostcodeIsReset('billingPostcodeIsReset', value)
      }
      newAddressDataModel={
        // Don't need to set a new address if billing address matches registered
        billingAddressMatchesRegistered ? null : addressTwoDataModel
      }
      persistedData={{ addressOne, addressTwo }}
    />,
  ];

  const soleTraderRoute = [
    <HomeAddressSelector
      key="homeAddress"
      postcodeSearched={homePostcodeSearched}
      setPostcodeSearched={value =>
        setPostcodeSearched('homePostcodeSearched', value)
      }
      setPostcodeIsReset={value =>
        setPostcodeIsReset('homePostcodeIsReset', value)
      }
      addressDataModel={addressOneDataModel}
      persistedData={addressOne}
    />,
    // We don't want it to show when the value is initially null, only if set to 'no' (false) by the
    // user
    propertyDuration === false && (
      <PreviousAddressSelector
        key="previousAddress"
        postcodeSearched={previousPostcodeSearched}
        setPostcodeSearched={value =>
          setPostcodeSearched('previousPostcodeSearched', value)
        }
        setPostcodeIsReset={value =>
          setPostcodeIsReset('previousPostcodeIsReset', value)
        }
        addressDataModel={addressTwoDataModel}
        persistedData={addressTwo}
      />
    ),
  ];

  const companyComponents = {
    0: mainRoute, // LC
    1: soleTraderRoute, // Sole Trader
    5: mainRoute, // LLP
    6: mainRoute, // PLC
  };

  return companyComponents[companyType];
};

Step3Routes.propTypes = {
  dispatch: PropTypes.func.isRequired,
  postcodeSearched: PropTypes.shape({
    manualCompanyPostcodeSearched: PropTypes.bool.isRequired,
    billingPostcodeSearched: PropTypes.bool.isRequired,
    homePostcodeSearched: PropTypes.bool.isRequired,
    previousPostcodeSearched: PropTypes.bool.isRequired,
  }).isRequired,
  hasPersistedData: PropTypes.shape({
    manualCompanyHasPersistedData: PropTypes.bool.isRequired,
    billingHasPersistedData: PropTypes.bool.isRequired,
  }).isRequired,
};

export default Step3Routes;
